/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { FormFieldValue, FormValidator } from 'src/core/types/form';
import * as booleanValidators from 'src/core/utils/formValidators/booleanValidators';
import * as fileValidators from 'src/core/utils/formValidators/fileValidators';
import * as numberValidators from 'src/core/utils/formValidators/numberValidators';
import * as stringArrayValidators from 'src/core/utils/formValidators/stringArrayValidators';
import * as stringValidators from 'src/core/utils/formValidators/stringValidators';

const messages = defineMessages({
    errorMessage: {
        id: 'portal.createRequiredValidator.errorMessage',
        defaultMessage: 'Value is required'
    }
});

/**
 * A factory to create a required validator. A required validator is simply
 * one that succeeds only if the value given is non empty or whitespace.
 */
export const createRequiredValidator = (options?: { deep: boolean }): FormValidator => (value: FormFieldValue) => {
    let validationResult: boolean;

    if (typeof value === 'number') {
        validationResult = numberValidators.createRequiredValidator()(value);
    }
    if (typeof value === 'boolean') {
        validationResult = booleanValidators.createRequiredValidator()(value);
    }
    if (typeof value === 'string') {
        validationResult = stringValidators.createRequiredValidator()(value);
    }
    if (value instanceof File) {
        validationResult = fileValidators.createRequiredValidator()(value);
    }
    if (Array.isArray(value)) {
        validationResult = stringArrayValidators.createRequiredValidator(options)(value);
    }

    if (!validationResult) {
        return { messageDescriptor: messages.errorMessage };
    }
};
